// Поиск по номеру телефона (кросс-партнёрский) + отвязка номера от адреса.
// Мета-админ находит номер, видит все его привязки к адресам у разных
// партнёров и отвязывает любую (flat_access удаляется, push-кеш пересобирается).
// После отвязки номер свободен — привязка к другому партнёру делается вне Sales.

import { useState } from "react";
import { gql } from "../lib/gql";

type Binding = {
  accessId: string;
  partnerCode: string;
  companyName: string;
  city: string;
  pointName: string;
  address: string;
  flatNumber: string;
  role: string;
  canReceiveCalls: boolean;
  createdAt: string;
};

type Resident = {
  id: string;
  phone: string;
  fullName: string;
  status: string;
  verifiedAt: string | null;
  appInstalls: number;
  bindings: Binding[];
};

const SEARCH_QUERY = `
  query AdminResidentsByPhone($phone: String!) {
    adminResidentsByPhone(phone: $phone) {
      id
      phone
      fullName
      status
      verifiedAt
      appInstalls
      bindings {
        accessId
        partnerCode
        companyName
        city
        pointName
        address
        flatNumber
        role
        canReceiveCalls
        createdAt
      }
    }
  }
`;

const UNBIND_MUTATION = `
  mutation AdminUnbindFlatAccess($accessId: ID!) {
    adminUnbindFlatAccess(accessId: $accessId)
  }
`;

export function PhonesPage() {
  const [phone, setPhone] = useState("");
  const [residents, setResidents] = useState<Resident[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [unbinding, setUnbinding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    if (!phone.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await gql<{ adminResidentsByPhone: Resident[] }>(SEARCH_QUERY, {
        phone: phone.trim(),
      });
      setResidents(data.adminResidentsByPhone);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function unbind(residentId: string, b: Binding) {
    if (!window.confirm(`Отвязать ${residentId ? "номер" : ""} от адреса «${b.companyName} · ${b.pointName} · кв. ${b.flatNumber}»?`)) {
      return;
    }
    setUnbinding(b.accessId);
    setError(null);
    try {
      await gql<{ adminUnbindFlatAccess: boolean }>(UNBIND_MUTATION, { accessId: b.accessId });
      setResidents((prev) =>
        (prev ?? []).map((r) =>
          r.id === residentId
            ? { ...r, bindings: r.bindings.filter((x) => x.accessId !== b.accessId) }
            : r,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUnbinding(null);
    }
  }

  return (
    <div className="page">
      <h2>Телефоны</h2>
      <p style={{ color: "var(--muted, #888)", fontSize: 14, maxWidth: 720 }}>
        Поиск резидента по номеру телефона (частичное совпадение) — по всем
        партнёрам. Отвязка снимает привязку номера к адресу: резидент
        перестаёт получать звонки на этот адрес. После отвязки номер можно
        привязать к адресу другого партнёра.
      </p>

      <form onSubmit={search} style={{ display: "flex", gap: 8, margin: "16px 0", maxWidth: 480 }}>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+7 777 123 45 67"
          style={{ flex: 1, padding: "8px 10px" }}
        />
        <button type="submit" disabled={loading || !phone.trim()}>
          {loading ? "Поиск…" : "Найти"}
        </button>
      </form>

      {error && <div className="error">{error}</div>}

      {residents !== null && residents.length === 0 && !loading && (
        <div style={{ color: "var(--muted, #888)" }}>Ничего не найдено.</div>
      )}

      {(residents ?? []).map((r) => (
        <div key={r.id} className="card" style={{ marginBottom: 16, maxWidth: 900 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <div>
              <strong style={{ fontSize: 16 }}>{r.phone}</strong>
              {r.fullName && <span style={{ marginLeft: 10 }}>{r.fullName}</span>}
            </div>
            <div style={{ fontSize: 13, color: "var(--muted, #888)" }}>
              {r.status} · установок приложения: {r.appInstalls}
              {r.verifiedAt ? " · подтверждён" : " · не подтверждён"}
            </div>
          </div>

          {r.bindings.length === 0 ? (
            <div style={{ marginTop: 10, color: "var(--muted, #888)", fontSize: 14 }}>
              Нет привязок к адресам.
            </div>
          ) : (
            <table className="data-table" style={{ width: "100%", marginTop: 10 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Партнёр</th>
                  <th style={{ textAlign: "left" }}>Адрес</th>
                  <th style={{ textAlign: "left" }}>Кв.</th>
                  <th style={{ textAlign: "left" }}>Роль</th>
                  <th style={{ textAlign: "left" }}>Звонки</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {r.bindings.map((b) => (
                  <tr key={b.accessId}>
                    <td>
                      {b.companyName}
                      <br />
                      <span style={{ color: "var(--muted, #888)", fontSize: 12 }}>#{b.partnerCode}</span>
                    </td>
                    <td>
                      {b.pointName}
                      <br />
                      <span style={{ color: "var(--muted, #888)", fontSize: 12 }}>{b.city} · {b.address}</span>
                    </td>
                    <td>{b.flatNumber}</td>
                    <td>{b.role}</td>
                    <td>{b.canReceiveCalls ? "да" : "нет"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        className="secondary"
                        onClick={() => unbind(r.id, b)}
                        disabled={unbinding === b.accessId}
                      >
                        {unbinding === b.accessId ? "…" : "Отвязать"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}
